/**
 * The list of files in flight, and the ones that did not make it.
 *
 * A refused file keeps its row rather than vanishing with a toast. The whole
 * point of checking in the browser is to tell someone what to change, and that
 * sentence has to still be on the screen when they go looking for the setting.
 */

import type { ReactElement } from 'react';
import { formatBytes } from '../../lib/format.js';
import { describeUploadStage, progressPercent } from './presentation.js';
import type { QueueItem, QueueState } from './useUploadQueue.js';

function stateLabel(item: QueueItem): string {
  switch (item.state) {
    case 'checking':
      return 'Checking the file';
    case 'waiting':
      return 'Waiting its turn';
    case 'working':
      return describeUploadStage(item.stage);
    case 'done':
      return 'Added to the library';
    case 'rejected':
      return 'Not accepted';
    case 'cancelled':
      return 'Cancelled';
    default:
      return 'Failed';
  }
}

function stateChip(state: QueueState): string {
  switch (state) {
    case 'done':
      return 'chip chip--success';
    case 'rejected':
    case 'failed':
      return 'chip chip--danger';
    case 'cancelled':
      return 'chip chip--neutral';
    default:
      return 'chip chip--accent';
  }
}

export interface UploadQueueListProps {
  items: readonly QueueItem[];
  onCancel: (id: string) => void;
  onDismiss: (id: string) => void;
  onClearSettled: () => void;
}

export function UploadQueueList({
  items,
  onCancel,
  onDismiss,
  onClearSettled,
}: UploadQueueListProps): ReactElement | null {
  if (items.length === 0) return null;

  const settled = items.filter(
    (item) => item.state === 'done' || item.state === 'rejected' || item.state === 'failed' || item.state === 'cancelled',
  ).length;

  return (
    <section className="panel">
      <div className="panel__head">
        <div>
          <h3 className="panel__title">Uploads</h3>
          <p className="panel__subtitle">
            One file at a time, so a slow connection finishes the first before it starts the second.
          </p>
        </div>
        {settled > 0 ? (
          <div className="panel__actions">
            <button type="button" className="button button--ghost button--small" onClick={onClearSettled}>
              Clear finished
            </button>
          </div>
        ) : null}
      </div>

      <ul className="upload-queue">
        {items.map((item) => {
          const percent = progressPercent(item.uploadedBytes, item.bytes);
          const inFlight = item.state === 'working' || item.state === 'checking' || item.state === 'waiting';

          return (
            <li className="upload-row" key={item.id}>
              <div className="upload-row__head">
                <span className="upload-row__name" title={item.fileName}>
                  {item.fileName}
                </span>
                <span className={stateChip(item.state)}>{stateLabel(item)}</span>
                {inFlight ? (
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    onClick={() => onCancel(item.id)}
                  >
                    Cancel
                  </button>
                ) : (
                  <button
                    type="button"
                    className="button button--ghost button--small"
                    onClick={() => onDismiss(item.id)}
                  >
                    Dismiss
                  </button>
                )}
              </div>

              {item.state === 'working' ? (
                <>
                  <div
                    className="upload-bar"
                    role="progressbar"
                    aria-valuenow={percent}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={`Uploading ${item.fileName}`}
                  >
                    <div className="upload-bar__fill" style={{ width: `${percent}%` }} />
                  </div>
                  <p className="upload-row__detail">
                    {percent}% · {formatBytes(item.uploadedBytes)} of {formatBytes(item.bytes)}
                  </p>
                </>
              ) : (
                <p className="upload-row__detail">{formatBytes(item.bytes)}</p>
              )}

              {item.title !== '' ? (
                <div
                  className={
                    item.state === 'cancelled' ? 'upload-row__note' : 'upload-row__note upload-row__note--bad'
                  }
                  role={item.state === 'rejected' || item.state === 'failed' ? 'alert' : undefined}
                >
                  <p className="upload-row__noteTitle">{item.title}</p>
                  <p className="upload-row__noteText">{item.message}</p>
                </div>
              ) : null}
            </li>
          );
        })}
      </ul>
    </section>
  );
}
